import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCelloData } from '../../../hooks/useCelloData';
import { usePaymentQueries } from '../../../hooks/usePaymentQueries';
import { useTaxYears } from '../../../hooks/useTaxYears';
import { useUserPlanStatus } from '../../../hooks/useUserPlanStatus';
import { useHelpscoutService } from '../../../services/helpscoutService';
import { AddonApiKey, type Addons, type Package, type UserPlans } from '../../../services/payment.types';
import { useTransactionService } from '../../../services/transaction.service';
import { convertCentsToEuros } from '../../../services/transformers';
import { QUERY_KEYS } from '../../../utils/queryKeys';
import { packages as packagesTemplate } from '../constants';

// Type definitions
export type SelectedTaxYear = {
    package: Package | null;
    expertReview: boolean;
};

export type SelectedTaxYears = {
    [year: string]: SelectedTaxYear;
};

export type AdditionalTransactions = {
    [year: string]: number;
};

export type PlanPageStep = 'initial' | 'diy' | 'expertChoice';

export type SelectedAddon = {
    title: string;
    key: keyof Addons;
    validUntil: string | null;
} | null;

export const usePlansPage = () => {
    const navigate = useNavigate();
    const { requestIndividualSetup } = useHelpscoutService();
    const location = useLocation();
    const { t } = useTranslation();
    const {
        createCheckoutSession,
        userPlans,
        isLoadingUserPlans,
        pricing,
        useCelloUccValidation,
        useIndividualPaymentPricing,
    } = usePaymentQueries();
    const typedUserPlans = userPlans as UserPlans;
    const { availableTaxYears: taxYears, isLoadingTaxYears, taxReports } = useTaxYears();
    const { hasAddon, isWeDoEverythingBoughtButNotValidated } = useUserPlanStatus();
    const { data: celloData, isLoading: isLoadingCelloHook, error: celloDataError } = useCelloData();
    const {
        data: celloValidationResult,
        isLoading: celloDiscountLoading,
        error: celloUccValidationError,
    } = useCelloUccValidation(celloData);
    const transactionService = useTransactionService();

    const isLoadingDiscount = isLoadingCelloHook || celloDiscountLoading;
    const celloDiscount = celloValidationResult?.data?.valid;

    // Process URL parameters
    const queryParams = new URLSearchParams(location.search);
    const year = queryParams.get('year');
    const transactions = queryParams.get('transactions');
    const isIndividualPaymentLink = queryParams.has('individual-payment-link');

    // Query for individual payment link custom pricing
    const {
        data: weDoEverythingCustomPricing,
        isLoading: isLoadingWeDoEverythingCustomPricing,
        error: weDoEverythingCustomPricingError,
    } = useIndividualPaymentPricing(isIndividualPaymentLink);

    const { data: allTimeTransactionData = { allTimeTotalTransactions: 0 }, isLoading: isLoadingAllTimeTransactions } =
        useQuery({
            queryKey: [QUERY_KEYS.ALL_TIME_ISSUE_COUNT],
            queryFn: () => transactionService.getAllTimeIssueCount(),
        });

    // Create packages with prices from API
    const packages = useMemo(() => {
        if (!pricing) {
            return packagesTemplate;
        }

        return packagesTemplate.map((template) => ({
            ...template,
            price: convertCentsToEuros(pricing.packages[template.id as keyof typeof pricing.packages]?.price || 0),
        }));
    }, [pricing]);

    const [planPageStep, setPlanPageStep] = useState<PlanPageStep>(
        isIndividualPaymentLink ? 'expertChoice' : year ? 'diy' : 'initial'
    );

    // Calculate initial state based on URL parameters
    const cheapestPackages = transactions
        ? packages.filter((pkg) => pkg.transactions >= parseInt(transactions, 10)).sort((a, b) => a.price - b.price)
        : null;
    const cheapestPackage = cheapestPackages && cheapestPackages.length > 0 ? cheapestPackages[0] : null;

    const defaultInitialTaxYear =
        year && transactions && cheapestPackages && cheapestPackage
            ? {
                  [year]: {
                      package: cheapestPackage,
                      expertReview: false,
                  },
              }
            : {};

    // Addon name mapping

    // State management
    const [selectedTaxYears, setSelectedTaxYears] = useState<SelectedTaxYears>(defaultInitialTaxYear);
    const [addons, setAddons] = useState<Addons>({
        expertReview: false,
        portfolioInsights: false,
        taxOptimization: false,
        weDoEverything: false, // Initialize weDoEverything
        individualSetup: false, // Initialize individualSetup
    });
    const [additionalTransactions, setAdditionalTransactions] = useState<AdditionalTransactions>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedAddon, setSelectedAddon] = useState<SelectedAddon>(null);


    const handleSelectDiy = useCallback(() => {
        setPlanPageStep('diy');
    }, [setPlanPageStep]);

    const handleNavigateToIntegrations = useCallback(() => {
        navigate('/integrations');
    }, [navigate]);

    const handleSelectExpert = useCallback(() => {
        setPlanPageStep('expertChoice');
    }, [setPlanPageStep]);

    const handleBackToInitialOptions = useCallback(() => {
        setPlanPageStep('initial');
        // Clear all selections when going back to initial step
        setSelectedTaxYears({});
        setAddons({
            portfolioInsights: false,
            taxOptimization: false,
            weDoEverything: false,
            individualSetup: false,
            expertReview: false,
        });
        setAdditionalTransactions({});
    }, [setPlanPageStep]);



    const individualSetupMutation = useMutation({
        mutationFn: requestIndividualSetup,
        onSuccess: () => {
            navigate('/plans/payment/success?individualSetup=true');
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const handleIndividualSetupRequest = useCallback(() => {
        // Toggle individual setup addon and reset other selections if enabling individualSetup
        setAddons((prev) => ({
            ...prev,
            individualSetup: !prev.individualSetup,
            // If we're enabling individualSetup, disable weDoEverything
            weDoEverything: prev.individualSetup ? prev.weDoEverything : false,
        }));

        // If we're enabling individualSetup, clear all selected tax years
        setSelectedTaxYears((prev) => {
            const wasIndividualSetup = addons.individualSetup;
            if (!wasIndividualSetup) {
                // We're enabling individualSetup, so clear all tax year selections
                return {};
            }
            // We're disabling individualSetup, keep existing selections
            return prev;
        });
    }, [addons.individualSetup]);

    // Derived state
    const localTaxYears = taxYears.map((year) => {
        const planKey = `taxReports.${year.year}` as string;
        return {
            ...year,
            purchased: typedUserPlans?.[planKey]?.status === 'active',
            transactions: year.transactions || 0, // Ensure transactions is always available
        };
    });

    const hasSelections = Object.keys(selectedTaxYears).length > 0 || Object.values(addons).some((value) => value);

    // Helper functions
    const isExpertReviewPurchased = useCallback(
        (year: string) => {
            const expertReviewKey = `expertReviews.${year}` as string;
            return (
                typedUserPlans?.[expertReviewKey]?.status === 'active' &&
                typedUserPlans?.[expertReviewKey]?.status !== 'refunded'
            );
        },
        [typedUserPlans]
    );

    // Map from display names to API keys for UI convenience
    const addonDisplayToApiKey: Record<string, AddonApiKey> = {
        'Portfolio Insights': AddonApiKey.ADVANCED_ANALYTICS,
        'Portfolio-Einblicke': AddonApiKey.ADVANCED_ANALYTICS,
        'Portfolio-Analyse': AddonApiKey.ADVANCED_ANALYTICS,
        'Advanced Analytics': AddonApiKey.ADVANCED_ANALYTICS,
        'Tax Optimization': AddonApiKey.TAX_OPTIMIZATION,
        Steueroptimierung: AddonApiKey.TAX_OPTIMIZATION,
    };

    const isAddonPurchased = useCallback(
        (addonName: string, addonKey: AddonApiKey | null = null) => {
            // Get the API key for this addon display name
            const apiKey = addonKey ?? addonDisplayToApiKey[addonName];
            if (!apiKey) return false;

            // First check with the API key in the local userPlans object
            const planKey = `addons.${apiKey}`;
            const isActiveInUserPlans = typedUserPlans?.[planKey]?.status === 'active';

            // Debug logging
            console.log(`Checking if addon ${addonName} (${apiKey}) is purchased:`, {
                isActiveInUserPlans,
                planKey,
                planData: typedUserPlans?.[planKey],
                userPlansKeys: typedUserPlans ? Object.keys(typedUserPlans) : [],
            });

            // Then check with the hasAddon function which also validates expiration
            const isActiveAndValid = hasAddon(apiKey);
            console.log(`hasAddon(${apiKey}) returned:`, isActiveAndValid);

            // Return true if the addon is active in either check
            return isActiveInUserPlans || isActiveAndValid;
        },
        [typedUserPlans, hasAddon, addonDisplayToApiKey]
    );

    const getAddonValidUntil = useCallback(
        (addonName: string, addonKeyParam: AddonApiKey | null = null) => {
            const apiKey = addonKeyParam ?? addonDisplayToApiKey[addonName];
            if (!apiKey) return undefined;

            const planKey = `addons.${apiKey}`;
            const validUntil = typedUserPlans?.[planKey]?.validUntil;

            // Debug logging
            console.log(`getAddonValidUntil for ${addonName} (${apiKey}):`, {
                planKey,
                validUntil,
                planData: typedUserPlans?.[planKey],
                typedUserPlansKeys: typedUserPlans ? Object.keys(typedUserPlans) : [],
            });

            return validUntil;
        },
        [typedUserPlans, addonDisplayToApiKey]
    );

    // Tax year selection handlers
    const handleTaxYearToggle = useCallback((year: string) => {
        setSelectedTaxYears((prev) => {
            const newState = { ...prev };
            if (newState[year]) {
                delete newState[year];
            } else {
                newState[year] = {
                    package: null,
                    expertReview: false,
                };
            }
            return newState;
        });
    }, []);

    const handlePackageSelect = useCallback(
        (year: string, packageId: string) => {
            // Find the package by ID
            const selectedPackage = packages.find((pkg) => pkg.id === packageId) || null;

            setSelectedTaxYears((prev) => {
                // Check if this package is already selected
                const isAlreadySelected = prev[year]?.package?.id === packageId;

                // If it's already selected, deselect it by setting package to null
                if (isAlreadySelected) {
                    return {
                        ...prev,
                        [year]: {
                            ...prev[year],
                            package: null,
                        },
                    };
                }

                // Otherwise, select the new package
                return {
                    ...prev,
                    [year]: {
                        ...prev[year],
                        package: selectedPackage,
                    },
                };
            });
        },
        [packages]
    );

    const handleExpertReviewToggle = useCallback((year: string) => {
        setSelectedTaxYears((prev) => ({
            ...prev,
            [year]: {
                ...prev[year],
                expertReview: !prev[year].expertReview,
            },
        }));
    }, []);

    const handleAdditionalTransactionsChange = useCallback((year: string, change: number) => {
        setAdditionalTransactions((prev) => ({
            ...prev,
            [year]: Math.max(0, (prev[year] || 0) + change),
        }));
    }, []);

    // Checkout handler
    const handleCheckout = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // If individual setup is selected, handle it separately
            if (addons.individualSetup) {
                await individualSetupMutation.mutateAsync();
                return;
            }

            // Check if all selected years either have a package selected or already purchased
            const incomplete = Object.entries(selectedTaxYears).some(
                ([year, data]) => !data.package && !typedUserPlans?.[`taxReports.${year}` as string]?.package
            );
            if (incomplete) {
                setError('Please select a package for all selected tax years');
                return;
            }

            // Only send years that have a new package selected or expert review added
            const yearsToSend = Object.entries(selectedTaxYears)
                .filter(([year, data]) => data.package || data.expertReview)
                .map(([year, data]) => {
                    // If we're only adding expert review to an existing package, send an empty package object
                    if (!data.package && data.expertReview) {
                        return {
                            taxYear: parseInt(year),
                            package: { id: '' }, // Empty package to indicate no new package purchase
                            expertReview: true,
                        };
                    }

                    // Otherwise find the package data for a new package purchase
                    const packageData = data.package
                        ? typeof data.package === 'string'
                            ? packages.find((p) => p.id === (data.package as unknown as string))
                            : data.package
                        : null;

                    if (!packageData && data.package !== null) {
                        throw new Error(`Could not find package data for year ${year}`);
                    }

                    return {
                        taxYear: parseInt(year),
                        package: packageData || { id: '' }, // Provide complete fallback
                        expertReview: data.expertReview,
                    };
                });

            if (
                yearsToSend.length === 0 &&
                !addons.portfolioInsights &&
                !addons.taxOptimization &&
                !addons.weDoEverything
            ) {
                setError('Please select at least one item to purchase');
                return;
            }

            const response = await createCheckoutSession({
                selectedYears: yearsToSend,
                addons: {
                    portfolioInsights: {
                        enabled: addons.portfolioInsights,
                        currentValidUntil: addons.portfolioInsights
                            ? getAddonValidUntil('Portfolio Insights')
                            : undefined,
                    },
                    taxOptimization: {
                        enabled: addons.taxOptimization,
                        currentValidUntil: addons.taxOptimization ? getAddonValidUntil('Tax Optimization') : undefined,
                    },
                    weDoEverything: {
                        enabled: addons.weDoEverything,
                        customPricing:
                            isIndividualPaymentLink && weDoEverythingCustomPricing?.weDoEverything
                                ? {
                                      price: weDoEverythingCustomPricing.weDoEverything.price,
                                      currency: weDoEverythingCustomPricing.weDoEverything.currency,
                                  }
                                : undefined,
                        isCustomPricing:
                            isIndividualPaymentLink && weDoEverythingCustomPricing?.weDoEverything ? true : false,
                    },
                },
                source: 'plans',
            });

            if (response?.url) {
                window.location.href = response.url;
            } else {
                setError('Failed to create checkout session');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            setError('An error occurred during checkout');
        } finally {
            setLoading(false);
        }
    }, [
        selectedTaxYears,
        addons,
        typedUserPlans,
        createCheckoutSession,
        getAddonValidUntil,
        individualSetupMutation,
    ]);

    // Calculate total transaction count using API data (all-time total)
    const getTotalTransactionCount = useCallback(() => {
        return allTimeTransactionData?.allTimeTotalTransactions || 0;
    }, [allTimeTransactionData?.allTimeTotalTransactions]);

    // Check if user can purchase "We Do Everything" addon
    // Considers transaction limits and validation status
    const canPurchaseWeDoEverything = useCallback((checkTransactionLimit: boolean = true) => {
        if (isWeDoEverythingBoughtButNotValidated()) {
            return false;
        }

        if (isIndividualPaymentLink) {
            return true;
        }

        // Only check transaction limit if requested (for second page)
        if (checkTransactionLimit) {
            const totalTransactions = getTotalTransactionCount();
            const weDoEverythingLimit = pricing?.addons?.weDoEverything?.transactions ?? 500;
            return totalTransactions <= weDoEverythingLimit;
        }

        return true;
    }, [
        getTotalTransactionCount,
        pricing?.addons?.weDoEverything?.transactions,
        isIndividualPaymentLink,
        isWeDoEverythingBoughtButNotValidated,
    ]);

    // Addon dialog handlers
    const handleAddonClick = useCallback(
        (title: string, addonKey: keyof Addons) => {
            if (addonKey === 'weDoEverything') {
                // For individual payment links, always allow purchase
                // For regular flow, check if user can purchase "We Do Everything" addon
                if (isIndividualPaymentLink || canPurchaseWeDoEverything(true)) {
                    // Directly toggle the 'weDoEverything' addon without showing a popup
                    setAddons((prev) => ({ ...prev, [addonKey]: !prev[addonKey] }));
                }
                // If they can't purchase it, do nothing (button should be disabled anyway)
            } else {
                // For other addons, show the confirmation popup
                const validUntil = getAddonValidUntil(title) ?? null;
                setSelectedAddon({ title, key: addonKey, validUntil });
            }
        },
        [getAddonValidUntil, setAddons, setSelectedAddon, canPurchaseWeDoEverything, isIndividualPaymentLink]
    );

    const handleAddonToggle = useCallback(
        (addonKey: keyof Addons) => {
            setAddons((prev) => {
                const newAddons = { ...prev, [addonKey]: !prev[addonKey] };

                // Handle mutual exclusivity between weDoEverything and individualSetup
                if (addonKey === 'weDoEverything' && newAddons.weDoEverything) {
                    newAddons.individualSetup = false;
                } else if (addonKey === 'individualSetup' && newAddons.individualSetup) {
                    newAddons.weDoEverything = false;
                }

                return newAddons;
            });

            // Clear tax year selections when enabling expert services
            if (addonKey === 'weDoEverything' && !addons.weDoEverything) {
                // We're enabling weDoEverything, clear all tax year selections
                setSelectedTaxYears({});
            }
        },
        [addons.weDoEverything]
    );

    const handleAddonPurchase = useCallback(() => {
        if (selectedAddon) {
            setAddons((prev) => ({ ...prev, [selectedAddon.key]: true }));
            setSelectedAddon(null);
        }
    }, [selectedAddon, setAddons, setSelectedAddon]);

    return {
        // State
        selectedTaxYears,
        addons,
        additionalTransactions,
        loading,
        error,
        selectedAddon,
        localTaxYears,
        hasSelections,
        isLoadingTaxYears,
        isLoadingUserPlans,
        userPlans: typedUserPlans,
        taxReports,
        year,
        setSelectedAddon,
        transactions,
        isLoadingDiscount,
        celloDiscount,
        isLoadingAllTimeTransactions,
        isIndividualPaymentLink,
        weDoEverythingCustomPricing,
        isLoadingWeDoEverythingCustomPricing,
        weDoEverythingCustomPricingError,

        // Helper functions
        isExpertReviewPurchased,
        isAddonPurchased,
        getAddonValidUntil,
        getTotalTransactionCount,
        canPurchaseWeDoEverything,

        // Event handlers
        handleTaxYearToggle,
        handlePackageSelect,
        handleExpertReviewToggle,
        handleAdditionalTransactionsChange,
        handleCheckout,
        handleAddonClick,
        handleAddonToggle,
        handleAddonPurchase,
        handleSelectDiy,
        handleNavigateToIntegrations,
        handleSelectExpert,
        handleBackToInitialOptions,
        planPageStep,
        handleIndividualSetupRequest,
        isIndividualSetupLoading: individualSetupMutation.isPending,
        individualSetupError: individualSetupMutation.error as Error | null,
    };
};
